import React, { useState, useEffect, useRef } from 'react';
import { X, ZoomIn } from 'lucide-react';

const PLACEHOLDER = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="700" height="450" viewBox="0 0 700 450"%3E%3Crect width="700" height="450" fill="%230072c6"/%3E%3Ctext x="350" y="225" text-anchor="middle" fill="%23e6edf5" font-size="18" font-family="Public Sans, Helvetica, Arial, Lucida, sans-serif"%3EImage unavailable%3C/text%3E%3C/svg%3E';

const gallery = [
  {
    id: 1,
    before: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=700&auto=format&fit=crop&q=80',
    after:  'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=700&auto=format&fit=crop&q=80',
    title: 'Full Shingle Replacement', location: 'Jacksonville, FL', type: 'Roof Replacement',
  },
  {
    id: 2,
    before: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=700&auto=format&fit=crop&q=80',
    after:  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=700&auto=format&fit=crop&q=80',
    title: 'Storm Damage Repair', location: 'Orange Park, FL', type: 'Roof Repair',
  },
  {
    id: 3,
    before: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=700&auto=format&fit=crop&q=80',
    after:  'https://images.unsplash.com/photo-1613977257363-707ba9348227?w=700&auto=format&fit=crop&q=80',
    title: 'Soft-Wash Clean', location: 'Ponte Vedra, FL', type: 'Roof Cleaning',
  },
  {
    id: 4,
    before: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=700&auto=format&fit=crop&q=80',
    after:  'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=700&auto=format&fit=crop&q=80',
    title: 'Commercial TPO Install', location: 'St. Augustine, FL', type: 'Commercial Roofing',
  },
  {
    id: 5,
    before: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=700&auto=format&fit=crop&q=80',
    after:  'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=700&auto=format&fit=crop&q=80',
    title: 'Hail Damage Repair', location: 'Fernandina Beach, FL', type: 'Roof Repair',
  },
  {
    id: 6,
    before: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=700&auto=format&fit=crop&q=80',
    after:  'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=700&auto=format&fit=crop&q=80',
    title: 'Metal Roof Installation', location: 'Green Cove Springs, FL', type: 'Roof Replacement',
  },
  {
    id: 7,
    before: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=700&auto=format&fit=crop&q=80',
    after:  'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=700&auto=format&fit=crop&q=80',
    title: 'Algae & Moss Removal', location: 'Atlantic Beach, FL', type: 'Roof Cleaning',
  },
  {
    id: 8,
    before: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=700&auto=format&fit=crop&q=80',
    after:  'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=700&auto=format&fit=crop&q=80',
    title: 'Pre-Sale Inspection & Repair', location: 'Nocatee, FL', type: 'Roof Repair',
  },
  {
    id: 9,
    before: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?w=700&auto=format&fit=crop&q=80',
    after:  'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=700&auto=format&fit=crop&q=80',
    title: 'Gutter Guard Installation', location: 'Middleburg, FL', type: 'Roof Cleaning',
  },
];

type Filter = 'All' | 'Roof Cleaning' | 'Roof Repair' | 'Roof Replacement' | 'Commercial Roofing';

function useIntersection(ref: React.RefObject<Element | null>, threshold = 0.15) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return visible;
}

function GalleryCard({ item, index, onLightbox }: {
  item: typeof gallery[0];
  index: number;
  onLightbox: (src: string, title: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersection(ref as React.RefObject<Element>);

  return (
    <div
      ref={ref}
      className="card gallery-card"
      style={{
        padding: 0, overflow: 'hidden', cursor: 'pointer',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.55s ease ${index * 0.1}s, transform 0.55s ease ${index * 0.1}s, box-shadow 0.25s ease`,
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', height: '220px' }}>
        {/* Before */}
        <div
          style={{ position: 'relative', overflow: 'hidden' }}
          onClick={() => onLightbox(item.before, `${item.title} — Before`)}
        >
          <img
            src={item.before}
            alt="Before"
            loading="lazy"
            onError={e => { (e.currentTarget as HTMLImageElement).src = PLACEHOLDER; }}
            style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s ease', display: 'block' }}
            onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.07)')}
            onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
          />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.35) 0%, transparent 60%)', pointerEvents: 'none' }} />
          <span style={{ position: 'absolute', bottom: '0.5rem', left: '0.5rem', background: 'rgba(0,0,0,0.8)', color: 'white', padding: '0.2rem 0.55rem', borderRadius: '0.3rem', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em' }}>BEFORE</span>
          <span style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'rgba(0,0,0,0.5)', color: 'white', padding: '0.2rem 0.4rem', borderRadius: '0.3rem', display: 'flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.68rem', opacity: 0, transition: 'opacity 0.2s' }} className="zoom-hint"><ZoomIn size={12} /> Zoom</span>
        </div>

        {/* After */}
        <div
          style={{ position: 'relative', overflow: 'hidden', borderLeft: '2px solid var(--bg-card)' }}
          onClick={() => onLightbox(item.after, `${item.title} — After`)}
        >
          <img
            src={item.after}
            alt="After"
            loading="lazy"
            onError={e => { (e.currentTarget as HTMLImageElement).src = PLACEHOLDER; }}
            style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s ease', display: 'block' }}
            onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.07)')}
            onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
          />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.25) 0%, transparent 60%)', pointerEvents: 'none' }} />
          <span style={{ position: 'absolute', bottom: '0.5rem', right: '0.5rem', background: '#1493e6', color: 'white', padding: '0.2rem 0.55rem', borderRadius: '0.3rem', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em' }}>AFTER</span>
        </div>
      </div>

      <div style={{ padding: '1rem 1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span className="badge badge-accent" style={{ marginBottom: '0.25rem' }}>{item.type}</span>
            <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)', margin: '0.25rem 0 0' }}>{item.title}</h3>
          </div>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{item.location}</span>
        </div>
      </div>
    </div>
  );
}

const GalleryPage: React.FC = () => {
  const [filter, setFilter] = useState<Filter>('All');
  const [lightbox, setLightbox] = useState<{ src: string; title: string } | null>(null);

  const filters: Filter[] = ['All', 'Roof Cleaning', 'Roof Repair', 'Roof Replacement', 'Commercial Roofing'];
  const filtered = gallery.filter(g => filter === 'All' || g.type === filter);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div>
      <div className="page-header" style={{ paddingTop: '7rem' }}>
        <h1 style={{ color: 'white', fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800, marginBottom: '1rem' }}>Before &amp; After Gallery</h1>
        <p style={{ color: '#e6edf5', maxWidth: '480px', margin: '0 auto' }}>Real results from real customers. Every photo is an actual job completed by the Cates Roofing team.</p>
      </div>

      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container-xl">
          {/* Filters */}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '3rem' }}>
            {filters.map((f, i) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  padding: '0.625rem 1.25rem', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: 500,
                  border: 'none', cursor: 'pointer',
                  background: filter === f ? 'linear-gradient(135deg, #1493e6, #0072c6)' : 'var(--bg-secondary)',
                  color: filter === f ? 'white' : 'var(--text-secondary)',
                  boxShadow: filter === f ? '0 4px 12px -3px rgba(8, 57, 129, 0.45)' : 'none',
                  transform: filter === f ? 'scale(1.04)' : 'scale(1)',
                  transition: 'all 0.2s',
                  animationDelay: `${i * 0.05}s`,
                }}
              >
                {f}
              </button>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1.75rem' }}>
            {filtered.map((item, i) => (
              <GalleryCard
                key={item.id}
                item={item}
                index={i}
                onLightbox={(src, title) => setLightbox({ src, title })}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', animation: 'fadeIn 0.2s ease' }}
        >
          <button
            onClick={() => setLightbox(null)}
            style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', cursor: 'pointer', borderRadius: '50%', width: '2.5rem', height: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.25)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.12)')}
          >
            <X size={20} />
          </button>
          <div
            style={{ maxWidth: '900px', width: '100%', animation: 'slideIn 0.25s ease' }}
            onClick={e => e.stopPropagation()}
          >
            <img
              src={lightbox.src}
              alt={lightbox.title}
              onError={e => { (e.currentTarget as HTMLImageElement).src = PLACEHOLDER; }}
              style={{ width: '100%', borderRadius: '1rem', boxShadow: '0 40px 80px rgba(0,0,0,0.6)', display: 'block' }}
            />
            <p style={{ color: 'white', textAlign: 'center', marginTop: '1rem', fontWeight: 600, fontSize: '1rem' }}>{lightbox.title}</p>
          </div>
        </div>
      )}

      <style>{`
        .gallery-card:hover { box-shadow: 0 16px 48px -8px rgba(0,0,0,0.2); transform: translateY(-4px) !important; }
      `}</style>
    </div>
  );
};

export default GalleryPage;
